import { Banner } from 'Components/shared/Banner';
import { observer } from 'mobx-react';
import { useReportIssue } from 'Modules/reportIssue/hooks/useReportIssue';
import { REPORT_ISSUE_CONNECTIVITY_SUBJECT } from 'Modules/reportIssue/index.constants';
import React, { useEffect, useRef, useState } from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { getBannerVariantProps } from '../helpers/getBannerVariantProps';
import { BannerLayoutProps } from '../index.types';
import { sendIpcLog } from 'Utils/ipcRendererEvents';
import {
  getBannerActions,
  INITIAL_MESSAGE,
  ONLINE_MESSAGE,
  PERSISTENT_BANNER_DURATION_MS,
  PERSISTENT_MESSAGE,
  SESSION_KEY_PERSISTENT_AFTER_REFRESH,
} from './index.constants';
import { ConnectionBannerWrapperProps } from './index.types';

export const ConnectionBannerWrapper = observer(
  ({ type, variant }: ConnectionBannerWrapperProps & BannerLayoutProps) => {
    const startPersistentAfterRefresh =
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(SESSION_KEY_PERSISTENT_AFTER_REFRESH) === 'true';

    const [isPersistent, setIsPersistent] = useState(
      startPersistentAfterRefresh
    );

    const { openReportIssueModal } = useReportIssue({
      subject: REPORT_ISSUE_CONNECTIVITY_SUBJECT,
      openWhenOffline: true,
    });

    const initialBannerTrackedRef = useRef(false);
    const persistentBannerTrackedRef = useRef(false);

    useEffect(() => {
      if (type !== 'offline') {
        setIsPersistent(false);
        initialBannerTrackedRef.current = false;
        persistentBannerTrackedRef.current = false;

        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(SESSION_KEY_PERSISTENT_AFTER_REFRESH);
        }

        return;
      }

      if (startPersistentAfterRefresh) {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(SESSION_KEY_PERSISTENT_AFTER_REFRESH);
        }

        setIsPersistent(true);

        if (!persistentBannerTrackedRef.current) {
          persistentBannerTrackedRef.current = true;
          pushToGTMDataLayer('connectivity_banner_persistent');
        }

        return;
      }

      if (!initialBannerTrackedRef.current) {
        initialBannerTrackedRef.current = true;
        pushToGTMDataLayer('connectivity_banner_initial');
      }

      const timer = setTimeout(() => {
        setIsPersistent(true);
        if (!persistentBannerTrackedRef.current) {
          persistentBannerTrackedRef.current = true;
          pushToGTMDataLayer('connectivity_banner_persistent');
        }
      }, PERSISTENT_BANNER_DURATION_MS);

      return () => clearTimeout(timer);
    }, [type, startPersistentAfterRefresh]);

    const handleRefresh = () => {
      pushToGTMDataLayer('connectivity_banner_refresh');
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(SESSION_KEY_PERSISTENT_AFTER_REFRESH, 'true');
      }

      sendIpcLog('info', 'Window was manually reloaded', {
        reason: 'connection banner refresh',
      });
      window.location.reload();
    };

    const handleContactSupport = () => {
      pushToGTMDataLayer('connectivity_banner_support');
      openReportIssueModal();
    };

    const action =
      type === 'offline' && isPersistent
        ? getBannerActions(handleRefresh, handleContactSupport)
        : undefined;

    return (
      <Banner
        isFullWidth
        type={type === 'online' ? 'success' : 'warning'}
        id="connection-banner-wrapper"
        message={
          type === 'online'
            ? ONLINE_MESSAGE
            : isPersistent
            ? PERSISTENT_MESSAGE
            : INITIAL_MESSAGE
        }
        action={action}
        showCloseButton={false}
        {...getBannerVariantProps(variant)}
      />
    );
  }
);
